import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { IModule } from '../../../../../interfaces/IModule';
import { EpisodeOfCareService } from '../../../../../services/episodeOfCare.service';
import { PatientService } from '../../../../../services/patient.service';
import { paths } from 'src/component-paths/component-paths';
import { combineLatest, debounceTime, Subject } from 'rxjs';
import { InitialAssessmentService } from 'src/app/services/initialAssessment.service';
import { DynamicQuestionnaireService } from 'src/app/services/dynamicQuestionnaire.service';
import { QuestionnaireHistoryItem } from 'src/app/interfaces/Questionnaire/IQuestionnaireHistory';
import { getQuestionnaireTemplateConfig, getQuestionnaireTemplateConfigByPath } from 'src/app/constants/questionnaire-template-registry';
import { QuestionnaireNavigationService } from 'src/app/services/questionnaireNavigation.service';
@Component({
  selector: 'app-initial-assessment-amen-section',
  templateUrl: './initial-assessment-amen-section.component.html',
  styleUrls: ['./initial-assessment-amen-section.component.css'],
  standalone: false,
})
export class InitialAssessmentAmenSectionComponent implements OnChanges {
  @Input() submenu?: IModule[];
  @Input() overviewItemsByTemplateIdInput: Record<
    number,
    QuestionnaireHistoryItem[]
  > | null = null;
  @Input() overviewLoadingInput: boolean | null = null;
  @Input() overviewErrorInput: string | null = null;
  componentPaths = paths;
  isLoading: boolean = false;
  localOverviewLoading = false;
  localOverviewError = '';
  localOverviewItemsByTemplateId: Record<number, QuestionnaireHistoryItem[]> =
    {};
  @Input() selectedOption?: string;
  @Output() selectedOptionChange = new EventEmitter<string>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['submenu'] && !this.usesExternalOverview()) {
      this.loadOverview();
    }
  }

  private scrollSubject = new Subject<Event>();

  ngAfterViewInit() {
    setTimeout(() => {
      combineLatest([
        this.initialAssessmentService.initialAssessmentLoadingSub$,
      ]).subscribe(([value1]) => {
        const sectionPath = this.submenu?.find(
          (sm) => sm.path == this.selectedOption
        )?.path;
        if (!value1 && sectionPath) {
          const element = document.getElementById(sectionPath);
          if (element) {
            // menu, patient header, title, margin
            const offset = element.offsetTop - 120 - 66 - 64 - 16;
            window.scroll({
              top: offset,
              behavior: 'smooth',
            });

            this.scrollSubject
              .pipe(
                debounceTime(100) // Adjust debounce time as needed (in milliseconds)
              )
              .subscribe(() => {
                // Your existing scroll logic here
                const shqa = document.getElementById(
                  paths.socialQuestionnaireAmen
                );
                const sho = document.getElementById(
                  paths.supportingDocumentsAmen
                );
                if (shqa) {
                  const rect = shqa.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.submenu?.find(
                      (sm) => sm.path == paths.socialQuestionnaireAmen
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
                if (sho) {
                  const rect = sho.getBoundingClientRect();
                  if (rect.top >= 120) {
                    this.selectedOption = this.submenu?.find(
                      (sm) => sm.path == paths.socialQuestionnaireAmen
                    )?.path;
                    this.selectedOptionChange.emit(this.selectedOption);
                  }
                }
              });
          }
        }
      });
    }, 500);
  }

  constructor(
    private readonly initialAssessmentService: InitialAssessmentService,
    private readonly eocService: EpisodeOfCareService,
    private readonly patientService: PatientService,
    private readonly router: Router,
    private readonly questionnaireService: DynamicQuestionnaireService,
    private readonly questionnaireNavigationService: QuestionnaireNavigationService,
  ) {}

  isVisitMode(): boolean {
    return (
      !!this.eocService.currentVisitId() &&
      (this.router.url.includes('episodeOfCare') ||
        this.router.url.includes('inPatientView'))
    );
  }

  currentVisitId(): string | number | undefined {
    return this.eocService.currentVisitId() || undefined;
  }

  currentPatientId(): string | undefined {
    return this.patientService.patientIDsignal() || undefined;
  }

  currentEpisodeOfCareId(): string | undefined {
    return this.eocService.currentEocId() || undefined;
  }

  handleVisitQuestionnaireSubmitted(): void {
    this.loadOverview();
  }

  getTemplateItems(templateId: number): QuestionnaireHistoryItem[] {
    return this.overviewItemsByTemplateId[templateId] ?? [];
  }

  get isOverviewLoading(): boolean {
    return this.overviewLoadingInput ?? this.localOverviewLoading;
  }

  get overviewError(): string {
    return this.overviewErrorInput ?? this.localOverviewError;
  }

  get overviewItemsByTemplateId(): Record<number, QuestionnaireHistoryItem[]> {
    return (
      this.overviewItemsByTemplateIdInput ??
      this.localOverviewItemsByTemplateId
    );
  }

  openVisitQuestionnaire(templateId: number): void {
    const routeSection = getQuestionnaireTemplateConfig(templateId)?.routeSection;

    if (!routeSection || !this.isVisitMode()) {
      return;
    }

    this.questionnaireNavigationService.setTemplateContext(templateId);
    this.questionnaireNavigationService.openScreen({
      source: this.router.url.includes('inPatientView')
        ? 'inPatientView'
        : 'episodeOfCare',
      mode: 'visit-add',
      routeSection,
      questionnaireTemplateId: templateId,
    });
  }

  openQuestionnaireSubmission(item: QuestionnaireHistoryItem): void {
    const templateId = Number(item.questionnaireTemplateId ?? 0);
    const questionnaireId = item.questionnaireId?.toString();
    const routeSection = getQuestionnaireTemplateConfig(templateId)?.routeSection;

    if (!templateId || !questionnaireId || !routeSection) {
      return;
    }

    this.questionnaireNavigationService.setTemplateContext(templateId);

    const source = this.isVisitMode()
      ? this.router.url.includes('inPatientView')
        ? 'inPatientView'
        : 'episodeOfCare'
      : 'profile';

    this.questionnaireNavigationService.openScreen({
      source,
      mode: this.isVisitMode() && item.canEdit ? 'visit-edit' : 'profile-readonly',
      routeSection,
      questionnaireTemplateId: templateId,
      questionnaireDataId: questionnaireId,
    });
  }

  @HostListener('window:scroll', ['$event'])
  onScroll(event: Event): void {
    this.scrollSubject.next(event);
  }

  private loadOverview(): void {
    if (this.usesExternalOverview()) {
      return;
    }

    const patientId = this.currentPatientId();
    const templateIds = this.collectTemplateIds(this.submenu);

    if (!patientId || !templateIds.length) {
      this.localOverviewItemsByTemplateId = {};
      return;
    }

    this.localOverviewLoading = true;
    this.localOverviewError = '';

    this.questionnaireService
      .getQuestionnaireOverview(patientId, {
        templateIds,
        ...(this.isVisitMode()
          ? {
              visitId: this.currentVisitId(),
              episodeCareId: this.currentEpisodeOfCareId(),
              includeDrafts: true,
            }
          : {}),
        section: paths.initialAssessments,
        forceRefresh: true,
      })
      .subscribe({
        next: (response) => {
          this.localOverviewItemsByTemplateId = response.templates.reduce(
            (accumulator: Record<number, QuestionnaireHistoryItem[]>, template) => {
              accumulator[template.questionnaireTemplateId] = template.items ?? [];
              return accumulator;
            },
            {},
          );
          this.localOverviewLoading = false;
        },
        error: () => {
          this.localOverviewItemsByTemplateId = {};
          this.localOverviewError = 'Could not load questionnaire overview.';
          this.localOverviewLoading = false;
        },
      });
  }

  private usesExternalOverview(): boolean {
    return this.overviewItemsByTemplateIdInput !== null;
  }

  private collectTemplateIds(modules?: IModule[]): number[] {
    return Array.from(
      new Set(
        modules
        ?.flatMap((module: IModule) => [
          getQuestionnaireTemplateConfigByPath(module.path)?.id,
          ...this.collectTemplateIds(module.childModules),
        ])
        .filter((id: number | undefined): id is number => !!id) ?? [],
      ),
    );
  }
}
